import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Edit2, Trash2, Save, X, AlertCircle, DollarSign } from 'lucide-react';
import { apiRequest } from '../config/api';
import API_CONFIG from '../config/api';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalCrear, setModalCrear] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [rolEditando, setRolEditando] = useState(null);
  
  // Formulario
  const [formData, setFormData] = useState({
    nombre: '',
    sueldo_sugerido: '',
    descripcion: ''
  });

  useEffect(() => {
    cargarRoles();
  }, []);

  const cargarRoles = async () => {
    try {
      const data = await apiRequest(API_CONFIG.ENDPOINTS.ROLES);
      setRoles(data || []);
    } catch (error) {
      console.error('Error cargando roles:', error);
      alert('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const abrirFormularioNuevo = () => {
    setRolEditando(null);
    setFormData({
      nombre: '',
      sueldo_sugerido: '',
      descripcion: ''
    });
    setModalCrear(true);
  };

  const iniciarEdicion = (rol) => {
    setRolEditando(rol);
    setFormData({
      nombre: rol.nombre,
      sueldo_sugerido: rol.sueldo_sugerido,
      descripcion: rol.descripcion || ''
    });
    setModalEditar(true);
  };

  const crearRol = async (e) => {
    e.preventDefault();
    
    try {
      await apiRequest(API_CONFIG.ENDPOINTS.ROLES, {
        method: 'POST',
        body: JSON.stringify({
          nombre: formData.nombre,
          sueldo_sugerido: parseFloat(formData.sueldo_sugerido),
          descripcion: formData.descripcion
        }),
      });
      
      alert('✅ Rol creado exitosamente');
      setModalCrear(false);
      resetForm();
      cargarRoles();
    } catch (error) {
      console.error('Error creando rol:', error);
      alert('Error al crear rol: ' + error.message);
    }
  };

  const guardarEdicion = async (e) => {
    e.preventDefault();
    
    try {
      await apiRequest(API_CONFIG.ENDPOINTS.ROL(rolEditando.id), {
        method: 'PUT',
        body: JSON.stringify({
          nombre: formData.nombre,
          sueldo_sugerido: parseFloat(formData.sueldo_sugerido),
          descripcion: formData.descripcion
        }),
      });
      
      alert('✅ Rol actualizado exitosamente');
      setModalEditar(false);
      setRolEditando(null);
      resetForm();
      cargarRoles();
    } catch (error) {
      console.error('Error actualizando rol:', error);
      alert('Error al actualizar rol: ' + error.message);
    }
  };

  const eliminarRol = async (rol) => {
    if (!window.confirm(`¿Estás seguro de eliminar el rol "${rol.nombre}"?\n\nNota: Solo se puede eliminar si no hay empleados asignados a este rol.`)) {
      return;
    }

    try {
      await apiRequest(API_CONFIG.ENDPOINTS.ROL(rol.id), {
        method: 'DELETE',
      });
      
      alert('✅ Rol eliminado exitosamente');
      cargarRoles();
    } catch (error) {
      console.error('Error eliminando rol:', error);
      alert('Error al eliminar rol: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      sueldo_sugerido: '',
      descripcion: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600 text-xl">Cargando roles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Roles</h1>
          <p className="text-gray-500 mt-1">Gestión de roles y salarios sugeridos</p>
        </div>
        <button
          onClick={abrirFormularioNuevo}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm"
        >
          <Plus size={20} />
          Nuevo Rol
        </button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>💡 Salarios Sugeridos:</strong> Al crear o editar un empleado, el salario se auto-completará según el rol seleccionado. Puedes modificarlo manualmente si es necesario.
            </p>
          </div>
        </div>
      </div>

      {/* Tarjetas de Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((rol) => (
          <div key={rol.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <Briefcase size={24} className="text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">{rol.nombre}</h3>
                  <p className="text-purple-100 text-sm">ID: {rol.id}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign size={18} className="text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Sueldo Sugerido</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  ${rol.sueldo_sugerido.toLocaleString('es-CO')}
                </p>
              </div>

              {rol.descripcion && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Descripción</p>
                  <p className="text-sm text-gray-700">{rol.descripcion}</p>
                </div>
              )}

              <div className="text-xs text-gray-400">
                Creado: {new Date(rol.created_at).toLocaleDateString('es-CO')}
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => iniciarEdicion(rol)}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  <Edit2 size={16} />
                  Editar
                </button>
                <button
                  onClick={() => eliminarRol(rol)}
                  className="flex-1 flex items-center justify-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Tarjeta vacía si no hay roles */}
        {roles.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
            <Briefcase size={64} className="mb-4" />
            <p className="text-lg">No hay roles registrados</p>
            <p className="text-sm">Comienza creando tu primer rol</p>
          </div>
        )}
      </div>

      {/* Modal Crear */}
      {modalCrear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Nuevo Rol</h2>
              <button onClick={() => { setModalCrear(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={crearRol} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Rol *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Barista, Cajero, Supervisor"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sueldo Sugerido *
                </label>
                <input
                  type="number"
                  step="1000"
                  value={formData.sueldo_sugerido}
                  onChange={(e) => setFormData({ ...formData, sueldo_sugerido: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1300000"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este valor se auto-completará al crear empleados con este rol
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Breve descripción de las responsabilidades..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  Crear Rol
                </button>
                <button
                  type="button"
                  onClick={() => { setModalCrear(false); resetForm(); }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {modalEditar && rolEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Editar Rol</h2>
              <button onClick={() => { setModalEditar(false); setRolEditando(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={guardarEdicion} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Rol *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sueldo Sugerido *
                </label>
                <input
                  type="number"
                  step="1000"
                  value={formData.sueldo_sugerido}
                  onChange={(e) => setFormData({ ...formData, sueldo_sugerido: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  Guardar Cambios
                </button>
                <button
                  type="button"
                  onClick={() => { setModalEditar(false); setRolEditando(null); resetForm(); }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
